import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';
import { Icon, Tooltip } from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { formatCost, formatNumber } from './shared';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 0.75rem 1rem;
  text-align: ${({ align }) => align || 'left'};
  font-weight: ${weights.bold};
  font-size: 0.875rem;
  color: ${colors.grey50};
  border-bottom: solid 2px ${colors.grey10};
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.875rem;
  border-bottom: solid 1px ${colors.grey10};
  vertical-align: middle;
`;

const WorkspaceCell = styled.div`
  display: flex;
  align-items: center;
`;

const WorkspaceLogo = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
`;

const WorkspaceImage = styled.img`
  max-height: 100%;
  object-fit: contain;
`;

const WorkspaceInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.75rem;
  min-width: 0;
`;

const WorkspaceName = styled.div`
  font-weight: ${weights.black};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const WorkspaceKey = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const SparklineCell = styled.div`
  height: 2.5rem;
`;

const sparklineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { display: false },
    y: { display: false },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      callbacks: {
        title: ([tooltip]) => {
          return new Date(tooltip.label).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          });
        },
        label: (context) => `${context.raw.toLocaleString()} tokens`,
      },
    },
  },
  elements: {
    point: { radius: 0, hoverRadius: 3 },
  },
};

function Sparkline({ overTime }) {
  const hasData = useMemo(() => (overTime || []).some((d) => d.tokens > 0), [overTime]);

  const chartData = useMemo(() => {
    if (!hasData || !overTime || overTime.length < 2) return null;
    return {
      labels: overTime.map((d) => d.date),
      datasets: [
        {
          data: overTime.map((d) => d.tokens),
          backgroundColor: colors.primary + '40',
          borderColor: colors.primary,
          borderWidth: 1.5,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [overTime, hasData]);

  if (!chartData) return null;

  return (
    <SparklineCell>
      <Line data={chartData} options={sparklineOptions} />
    </SparklineCell>
  );
}

export default function Workspaces({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiUsage.getByWorkspace({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;
  if (data.length === 0) return null;

  return (
    <div>
      <Table>
        <thead>
          <tr>
            <Th>Workspace</Th>
            <Th align="right">
              <Tooltip message="Members who have used the AI assistant in this period">
                <span>AI Members</span>
              </Tooltip>
            </Th>
            <Th align="right">
              <Tooltip message="Percentage of workspace members using the AI assistant">
                <span>Adoption</span>
              </Tooltip>
            </Th>
            <Th align="right">Conversations</Th>
            <Th align="right">Requests</Th>
            <Th align="right">
              <Tooltip message="Average cost per active AI member">
                <span>Avg Cost</span>
              </Tooltip>
            </Th>
            <Th align="right">Total Cost</Th>
            <Th align="right">
              <Tooltip message="Share of cost from the top 20% of active AI members. Values near 100% indicate concentrated usage among a few power users.">
                <span>Top 20% Share</span>
              </Tooltip>
            </Th>
            <Th>Usage</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.workspaceId}>
              <Td>
                <WorkspaceCell>
                  <WorkspaceLogo>
                    {row.workspaceImageUrl ? (
                      <WorkspaceImage src={row.workspaceImageUrl} alt={row.workspaceName} />
                    ) : (
                      <Icon icon="building" style={{ fontSize: '2rem', color: colors.grey10 }} />
                    )}
                  </WorkspaceLogo>
                  <WorkspaceInfo>
                    <WorkspaceName>{row.workspaceName}</WorkspaceName>
                    <WorkspaceKey>{row.workspaceKey}</WorkspaceKey>
                  </WorkspaceInfo>
                </WorkspaceCell>
              </Td>
              <Td align="right">
                {formatNumber(row.activeMembers)} / {formatNumber(row.totalMembers)}
              </Td>
              <Td align="right">
                {row.totalMembers > 0 ? ((row.activeMembers / row.totalMembers) * 100).toFixed(0) : 0}%
              </Td>
              <Td align="right">{formatNumber(row.conversations)}</Td>
              <Td align="right">{formatNumber(row.requests)}</Td>
              <Td align="right">{formatCost(row.avgCostPerMember)}</Td>
              <Td align="right">{formatCost(row.totalCost)}</Td>
              <Td align="right">{row.top20Share > 0 ? (row.top20Share * 100).toFixed(0) + '%' : '—'}</Td>
              <Td>
                <Sparkline overTime={row.overTime} />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
